import { isNaN } from 'formik'
import moment from 'moment'
import React, { useCallback, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { EuiButton, EuiCallOut, EuiCheckbox, EuiComboBox, EuiComboBoxOptionOption, EuiDatePicker, EuiFieldNumber, EuiFieldText, EuiFlexGroup, EuiFlexItem, EuiForm, EuiFormRow, EuiPage, EuiPageBody, EuiPageHeader, EuiPageHeaderSection, EuiPanel, EuiRadio, EuiSpacer, EuiSteps, EuiSuperSelect, EuiText, EuiTextArea, EuiTitle } from '@elastic/eui'
import { EuiStepInterface } from '@elastic/eui/src/components/steps/step'

import { AIPlanBlueprintStatus } from 'api/interfaces/AIPlanBlueprint'
import { useBeginCreateAIPlanBlueprintMutation, useGetAIPlanBlueprintsQuery } from 'api/rtkQueryApi/platform/aiPlanBlueprintsApi'
import { useBeginCreateAIPlanMutation, useGetAIPlansQuery, useGetOffAdCritterPlatformsQuery } from 'api/rtkQueryApi/platform/aiPlansApi'
import { useGetOrganizationQuery } from 'api/rtkQueryApi/platform/organizationsApi'
import { showWarningToast } from 'app/appSlice'
import { RootState } from 'app/rootReducer'
import { AcEuiFormRow } from 'components/Forms/AcEuiFormRow'
import { reset as resetBlueprint } from 'features/planner/CreateAIPlanBlueprintSlice'
import { toExcelLetter } from 'utils/Formatters'

import history from '../../services/HistoryService'
import { AIPlanCreateStep, BudgetModeOption, ChannelModeOption, OutputFormatOption, PersonaModeOption, TimeframeOption, reset, setAdditionalGuidance, setBlueprintId, setBudgetMode, setChannelMode, setHighBudget, setLowBudget, setMediumBudget, setCustomDurationMonths, setMonthlyBudget, setPersonaMode, setPlanStartDate, setSelectedChannels, setSelectedPersonas, setOutputFormat, setStep, setTimeframeOption } from './CreateAIPlanSlice'

const CHANNEL_DEFINITIONS = [
  { value: 'TV', label: 'TV (CTV/OTT)' },
  { value: 'Billboard', label: 'Billboard (Digital Roadside)' },
  // HIDE_STREAMING_AUDIO_FROM_AI_PLAN - uncomment to re-enable Audio in the AI plan create wizard
  // { value: 'Audio', label: 'Streaming Audio' },
  { value: 'Internet', label: 'Internet (Display)' }
]

export const AIPlanCreatePage: React.FC = () => {
  const formatter = new Intl.NumberFormat('en-US', {
    maximumFractionDigits: 0
  })

  const dispatch = useDispatch()
  const createAIPlan = useSelector((state: RootState) => state.createAIPlan)
  const blueprintState = useSelector((state: RootState) => state.createAIPlanBlueprint)
  const isInstantPlan = blueprintState.instantPlanEnabled
  const { currentOrganization, currentAccount, isStaffUser, showInternalView } = useSelector((state: RootState) => state.app)
  const { data: plans } = useGetAIPlansQuery({ organizationId: currentOrganization?.id ?? '', showHidden: isStaffUser && showInternalView })
  const { data: blueprints } = useGetAIPlanBlueprintsQuery({ organizationId: currentOrganization?.id ?? '', showHidden: isStaffUser && showInternalView })
  const { data: offAdCritterPlatforms } = useGetOffAdCritterPlatformsQuery()
  const offAdCritterChannels = (offAdCritterPlatforms ?? []).map(p => ({ value: p.name, label: p.name }))
  const [beginCreateAIPlan, beginCreateAIPlanResult] = useBeginCreateAIPlanMutation()
  const [beginCreateAIPlanBlueprint, beginCreateBlueprintResult] = useBeginCreateAIPlanBlueprintMutation()
  const { data: organizationData } = useGetOrganizationQuery()
  const minimumBudget = organizationData?.aiPlanMinimumBudget ?? 2500
  const [showWarning, setShowWarning] = React.useState(false)
  const [warningText, setWarningText] = React.useState('')

  const isCreateLoading = beginCreateAIPlanResult.isLoading || beginCreateBlueprintResult.isLoading

  const accountPlans = plans?.filter(p => p.accountId === currentAccount?.id) ?? []
  const accountBlueprints = blueprints?.filter(b => b.accountId === currentAccount?.id) ?? []
  const readyBlueprints = accountBlueprints.filter(b => b.status === AIPlanBlueprintStatus.Ready)
  const selectedBlueprint = readyBlueprints.find(b => b.id === createAIPlan.blueprintId)

  useEffect(() => {
    return () => {
      dispatch(reset())
      if (isInstantPlan) {
        dispatch(resetBlueprint())
      }
    }
  }, [dispatch, isInstantPlan])

  const getNumberOnly = (value: string) => {
    return parseInt(value.replaceAll(',', ''))
  }

  const getDurationMonths = (): number => {
    switch (createAIPlan.timeframeOption) {
      case TimeframeOption.TwelveMonths:
        return 12
      case TimeframeOption.SixMonths:
        return 6
      case TimeframeOption.Other:
        return createAIPlan.customDurationMonths ?? 1
    }
  }

  const isCustomBudget = createAIPlan.budgetMode === BudgetModeOption.Custom

  const isThreePlansThreeBudgets = createAIPlan.outputFormat === OutputFormatOption.ThreePlansThreeBudgets
  const isMultiPlan = createAIPlan.outputFormat !== OutputFormatOption.OnePlan

  const callCreateAIPlan = useCallback(async () => {
    if (!currentOrganization || !currentAccount) {
      return
    }

    if (isInstantPlan) {
      const blueprintLetter = toExcelLetter(accountBlueprints.length)
      await beginCreateAIPlanBlueprint({
        organizationId: currentOrganization.id,
        params: {
          accountId: currentAccount.id,
          name: `${currentAccount.name} Blueprint ${blueprintLetter}`,
          companyName: currentAccount.name,
          companyDomain: blueprintState.companyDomain.trim(),
          employees: blueprintState.employees,
          revenue: blueprintState.revenue,
          userProvidedInsights: blueprintState.userProvidedInsights.trim() || undefined,
          instantPlan: {
            budgetMode: createAIPlan.budgetMode,
            monthlyBudget: isCustomBudget ? createAIPlan.monthlyBudget : undefined,
            channelMode: createAIPlan.channelMode,
            selectedChannels: createAIPlan.channelMode !== ChannelModeOption.AiRecommended ? createAIPlan.selectedChannels : undefined,
            personaMode: createAIPlan.personaMode,
            planDurationInMonths: getDurationMonths(),
            planStartDate: moment(createAIPlan.planStartDate).toISOString(),
            additionalGuidance: createAIPlan.additionalGuidance.trim() || undefined
          }
        }
      })
      return
    }

    const budgetFields = isCustomBudget ? (isThreePlansThreeBudgets ? { highBudget: createAIPlan.highBudget, mediumBudget: createAIPlan.mediumBudget, lowBudget: createAIPlan.lowBudget } : { monthlyBudget: createAIPlan.monthlyBudget }) : {}
    await beginCreateAIPlan({
      organizationId: currentOrganization.id,
      params: {
        accountId: currentAccount.id,
        blueprintId: createAIPlan.blueprintId,
        name: `${currentAccount.name} Plan ${accountPlans.length + 1}`,
        outputFormat: createAIPlan.outputFormat,
        budgetMode: createAIPlan.budgetMode,
        ...budgetFields,
        channelMode: createAIPlan.channelMode,
        selectedChannels: createAIPlan.channelMode !== ChannelModeOption.AiRecommended ? createAIPlan.selectedChannels : undefined,
        personaMode: createAIPlan.personaMode,
        selectedPersonas: createAIPlan.personaMode === PersonaModeOption.Selected && createAIPlan.selectedPersonas.length > 0 ? createAIPlan.selectedPersonas : undefined,
        planDurationInMonths: getDurationMonths(),
        planStartDate: moment(createAIPlan.planStartDate).toISOString(),
        additionalGuidance: createAIPlan.additionalGuidance.trim() || undefined
      }
    })
  }, [createAIPlan, currentOrganization, currentAccount, beginCreateAIPlan, isInstantPlan, blueprintState, beginCreateAIPlanBlueprint])

  const handleContinue = async () => {
    setShowWarning(false)
    switch (createAIPlan.step) {
      case AIPlanCreateStep.SelectBasis:
        dispatch(setStep(AIPlanCreateStep.OutputFormat))
        break
      case AIPlanCreateStep.OutputFormat:
        if (isCustomBudget) {
          const formattedMinimum = `$${minimumBudget.toLocaleString()}`
          if (isThreePlansThreeBudgets && !isInstantPlan) {
            const invalidBudget = [createAIPlan.highBudget, createAIPlan.mediumBudget, createAIPlan.lowBudget].some(b => !b || b < minimumBudget || Number.isNaN(b))
            if (invalidBudget) {
              setShowWarning(true)
              setWarningText(`All three budgets must be at least ${formattedMinimum}.`)
              break
            }
            const { highBudget, mediumBudget, lowBudget } = createAIPlan
            if (highBudget && mediumBudget && lowBudget && !(highBudget >= mediumBudget && mediumBudget >= lowBudget)) {
              setShowWarning(true)
              setWarningText('Budgets must be in order: high → medium → low.')
              break
            }
          } else {
            if (!createAIPlan.monthlyBudget || createAIPlan.monthlyBudget < minimumBudget || Number.isNaN(createAIPlan.monthlyBudget)) {
              setShowWarning(true)
              setWarningText(`The minimum budget is ${formattedMinimum}.`)
              break
            }
          }
        }
        dispatch(setStep(AIPlanCreateStep.Channels))
        break
      case AIPlanCreateStep.Channels:
        dispatch(setStep(AIPlanCreateStep.Personas))
        break
      case AIPlanCreateStep.Personas:
        dispatch(setStep(AIPlanCreateStep.Timeframe))
        break
      case AIPlanCreateStep.Timeframe:
        dispatch(setStep(AIPlanCreateStep.AdditionalGuidance))
        break
      case AIPlanCreateStep.AdditionalGuidance:
        await callCreateAIPlan()
        history.push(isInstantPlan ? '/ai/blueprints' : '/ai/plans')
        break
      default:
        break
    }
  }

  const handleBack = () => {
    const currentPlans = plans?.filter(p => p.accountId === currentAccount?.id) ?? []

    switch (createAIPlan.step) {
      case AIPlanCreateStep.SelectBasis:
        if (currentPlans.length > 0) {
          history.push('/ai/plans')
        } else {
          history.push('/ai/plans/welcome')
        }
        break
      case AIPlanCreateStep.OutputFormat:
        setShowWarning(false)
        if (isInstantPlan) {
          history.push('/ai/blueprints/create')
        } else {
          dispatch(setStep(AIPlanCreateStep.SelectBasis))
        }
        break
      case AIPlanCreateStep.Channels:
        setShowWarning(false)
        dispatch(setStep(AIPlanCreateStep.OutputFormat))
        break
      case AIPlanCreateStep.Personas:
        setShowWarning(false)
        dispatch(setStep(AIPlanCreateStep.Channels))
        break
      case AIPlanCreateStep.Timeframe:
        dispatch(setStep(AIPlanCreateStep.Personas))
        break
      case AIPlanCreateStep.AdditionalGuidance:
        dispatch(setStep(AIPlanCreateStep.Timeframe))
        break
      default:
        break
    }
  }

  const getConfirmText = (step: AIPlanCreateStep) => {
    switch (step) {
      case AIPlanCreateStep.AdditionalGuidance:
        if (isInstantPlan) return 'Create Blueprint & Plan'
        return isMultiPlan ? 'Generate Plans' : 'Generate Plan'
      default:
        return 'Continue'
    }
  }

  const shouldDisableNext = useCallback(() => {
    switch (createAIPlan.step) {
      case AIPlanCreateStep.SelectBasis:
        return !createAIPlan.blueprintId
      case AIPlanCreateStep.OutputFormat:
        if (isCustomBudget) {
          if (isThreePlansThreeBudgets) {
            return [createAIPlan.highBudget, createAIPlan.mediumBudget, createAIPlan.lowBudget].some(b => !b || Number.isNaN(b))
          }
          return !createAIPlan.monthlyBudget || Number.isNaN(createAIPlan.monthlyBudget)
        }
        return false
      case AIPlanCreateStep.Channels:
        if (createAIPlan.channelMode !== ChannelModeOption.AiRecommended) {
          return createAIPlan.selectedChannels.length === 0
        }
        return false
      case AIPlanCreateStep.Personas:
        return createAIPlan.personaMode === PersonaModeOption.Selected && createAIPlan.selectedPersonas.length === 0
      case AIPlanCreateStep.Timeframe:
        if (createAIPlan.timeframeOption === TimeframeOption.Other) {
          return !createAIPlan.customDurationMonths || createAIPlan.customDurationMonths < 1
        }
        return false
      default:
        return false
    }
  }, [createAIPlan])

  const stepOrder = isInstantPlan
    ? [AIPlanCreateStep.OutputFormat, AIPlanCreateStep.Channels, AIPlanCreateStep.Personas, AIPlanCreateStep.Timeframe, AIPlanCreateStep.AdditionalGuidance]
    : [AIPlanCreateStep.SelectBasis, AIPlanCreateStep.OutputFormat, AIPlanCreateStep.Channels, AIPlanCreateStep.Personas, AIPlanCreateStep.Timeframe, AIPlanCreateStep.AdditionalGuidance]

  const getStepStatus = (step: AIPlanCreateStep): 'current' | 'complete' | 'incomplete' => {
    const currentIndex = stepOrder.indexOf(createAIPlan.step)
    const targetIndex = stepOrder.indexOf(step)

    if (currentIndex === targetIndex) return 'current'
    if (targetIndex < currentIndex) return 'complete'
    return 'incomplete'
  }

  const blueprintSelectOptions = [...readyBlueprints]
    .sort((a, b) => new Date(b.created).getTime() - new Date(a.created).getTime())
    .map(b => ({
      value: b.id,
      inputDisplay: `${b.name} - Created: ${moment(b.created).calendar()}`,
      dropdownDisplay: (
        <>
          <strong>{b.name}</strong>
          <EuiText size='s' color='subdued'>
            <p>Created: {moment(b.created).calendar()}</p>
          </EuiText>
        </>
      )
    }))

  const personaOptions: EuiComboBoxOptionOption[] = (selectedBlueprint?.personas ?? []).map(p => ({ label: p.name, key: p.id }))

  const getBudgetSummary = () => {
    if (isThreePlansThreeBudgets) {
      if (!isCustomBudget) return '3 plans - AI recommended budgets'
      return `3 plans - High: $${formatter.format(createAIPlan.highBudget ?? 0)} / Med: $${formatter.format(createAIPlan.mediumBudget ?? 0)} / Low: $${formatter.format(createAIPlan.lowBudget ?? 0)}`
    }
    if (createAIPlan.outputFormat === OutputFormatOption.ThreePlansOneBudget) {
      if (!isCustomBudget) return '3 plans, 1 budget - AI recommended'
      return `3 plans, 1 budget - $${formatter.format(createAIPlan.monthlyBudget ?? 0)} /mo`
    }
    if (!isCustomBudget) return 'AI recommended budget'
    return `$${formatter.format(createAIPlan.monthlyBudget ?? 0)} /mo`
  }

  const getChannelsSummary = () => {
    if (createAIPlan.channelMode === ChannelModeOption.AiRecommended) {
      return 'AI recommended channels'
    }
    const allChannels = [...CHANNEL_DEFINITIONS, ...offAdCritterChannels]
    const labels = createAIPlan.selectedChannels.map(ch => allChannels.find(d => d.value === ch)?.label ?? ch)
    return labels.join(', ')
  }

  const getTimeframeSummary = () => {
    const duration = getDurationMonths()
    return `${duration} month${duration !== 1 ? 's' : ''}`
  }

  const steps: EuiStepInterface[] = [
    ...(!isInstantPlan
      ? [
          {
            title: 'Select Blueprint',
            status: getStepStatus(AIPlanCreateStep.SelectBasis),
            titleSize: 's' as const,
            children:
              selectedBlueprint && getStepStatus(AIPlanCreateStep.SelectBasis) === 'complete' ? (
                <EuiText size='m'>
                  <p>{selectedBlueprint.name}</p>
                </EuiText>
              ) : (
                <></>
              )
          }
        ]
      : []),
    {
      title: 'Budgeting',
      status: getStepStatus(AIPlanCreateStep.OutputFormat),
      titleSize: 's',
      children:
        getStepStatus(AIPlanCreateStep.OutputFormat) === 'complete' ? (
          <EuiText size='m'>
            <p>{getBudgetSummary()}</p>
          </EuiText>
        ) : (
          <></>
        )
    },
    {
      title: 'Channels',
      status: getStepStatus(AIPlanCreateStep.Channels),
      titleSize: 's',
      children:
        getStepStatus(AIPlanCreateStep.Channels) === 'complete' ? (
          <EuiText size='m'>
            <p>{getChannelsSummary()}</p>
          </EuiText>
        ) : (
          <></>
        )
    },
    {
      title: 'Personas',
      status: getStepStatus(AIPlanCreateStep.Personas),
      titleSize: 's',
      children:
        getStepStatus(AIPlanCreateStep.Personas) === 'complete' ? (
          <EuiText size='m'>
            <p>{createAIPlan.personaMode === PersonaModeOption.All ? 'Using all personas' : createAIPlan.personaMode === PersonaModeOption.Ignore ? 'Ignoring personas' : `${createAIPlan.selectedPersonas.length} persona(s) selected`}</p>
          </EuiText>
        ) : (
          <></>
        )
    },
    {
      title: 'Timeframe',
      status: getStepStatus(AIPlanCreateStep.Timeframe),
      titleSize: 's',
      children:
        getStepStatus(AIPlanCreateStep.Timeframe) === 'complete' ? (
          <EuiText size='m'>
            <p>{getTimeframeSummary()}</p>
          </EuiText>
        ) : (
          <></>
        )
    },
    {
      title: 'Guidance Prompt',
      status: getStepStatus(AIPlanCreateStep.AdditionalGuidance),
      titleSize: 's',
      children: <></>
    }
  ]

  const renderSelectBasisStep = () => (
    <>
      <EuiTitle size='s'>
        <h2>Step 1: Select Blueprint</h2>
      </EuiTitle>
      <EuiText>Choose an existing blueprint to use as the foundation for your AI plan.</EuiText>
      <EuiSpacer />
      {readyBlueprints.length > 0 ? (
        <EuiForm>
          <EuiFormRow label='Blueprint' fullWidth>
            <EuiSuperSelect options={blueprintSelectOptions} valueOfSelected={createAIPlan.blueprintId} onChange={value => dispatch(setBlueprintId(value))} fullWidth placeholder='Select a blueprint' />
          </EuiFormRow>
        </EuiForm>
      ) : (
        <>
          <EuiCallOut title='No blueprints available' color='warning'>
            <p>
              You need at least one ready blueprint to create an AI plan.{' '}
              <EuiButton size='s' onClick={() => history.push('/ai/blueprints/create')}>
                Create a Blueprint
              </EuiButton>
            </p>
          </EuiCallOut>
        </>
      )}
    </>
  )

  const isBudgetBelowMinimum = (value: number | undefined) => !!value && value > 0 && value < minimumBudget

  const renderBudgetInputField = (label: string, value: number | undefined, onChange: (val: number | undefined) => void, disabled = false) => (
    <AcEuiFormRow label={label} isInvalid={isBudgetBelowMinimum(value)} error={`Minimum budget is $${minimumBudget.toLocaleString()}.`}>
      <EuiFieldText
        prepend='$'
        disabled={disabled}
        value={value && value > 0 ? formatter.format(value) : ''}
        onChange={e => {
          if (!e.target.value) {
            onChange(undefined)
            return
          }
          const num = getNumberOnly(e.target.value)
          if (!isNaN(num)) {
            onChange(num)
          }
        }}
      />
    </AcEuiFormRow>
  )

  const handleOutputFormatChange = (format: OutputFormatOption) => {
    dispatch(setOutputFormat(format))
    dispatch(setBudgetMode(BudgetModeOption.AiRecommended))
  }

  const renderBudgetModeRadios = (formatOption: OutputFormatOption) => {
    const isActiveFormat = createAIPlan.outputFormat === formatOption
    const budgetLabel = formatOption === OutputFormatOption.ThreePlansThreeBudgets ? 'Budgets' : 'Budget'
    return (
      <div style={{ marginLeft: 24, marginTop: 8 }}>
        <EuiRadio id={`ai-recommended-budget-${formatOption}`} disabled={!isActiveFormat} onChange={() => dispatch(setBudgetMode(BudgetModeOption.AiRecommended))} checked={isActiveFormat && !isCustomBudget} label={`AI Recommended ${budgetLabel}`} />
        <EuiRadio id={`custom-budget-${formatOption}`} disabled={!isActiveFormat} onChange={() => dispatch(setBudgetMode(BudgetModeOption.Custom))} checked={isActiveFormat && isCustomBudget} label={`Custom ${budgetLabel}`} />
      </div>
    )
  }

  const budgetingStepNumber = isInstantPlan ? 1 : 2

  const renderBudgetingStep = () => (
    <>
      <EuiTitle size='s'>
        <h2>Step {budgetingStepNumber}: Budgeting</h2>
      </EuiTitle>
      <EuiText>{isInstantPlan ? 'Choose the budget approach for your plan.' : 'Choose how many plans to generate and the budget approach.'}</EuiText>
      <EuiSpacer />
      {!isInstantPlan && <EuiRadio id='three-plans-three-budgets' onChange={() => handleOutputFormatChange(OutputFormatOption.ThreePlansThreeBudgets)} checked={isThreePlansThreeBudgets} label='3 plans, 3 budgets' />}
      {isThreePlansThreeBudgets && (
        <>
          {renderBudgetModeRadios(OutputFormatOption.ThreePlansThreeBudgets)}
          {isCustomBudget && (
            <div style={{ marginLeft: 48, marginTop: 8 }}>
              <EuiForm>
                {renderBudgetInputField('Target High Budget', createAIPlan.highBudget, val => dispatch(setHighBudget(val)), false)}
                {renderBudgetInputField('Target Medium Budget', createAIPlan.mediumBudget, val => dispatch(setMediumBudget(val)), false)}
                {renderBudgetInputField('Target Low Budget', createAIPlan.lowBudget, val => dispatch(setLowBudget(val)), false)}
              </EuiForm>
            </div>
          )}
        </>
      )}
      {!isInstantPlan && <EuiRadio id='three-plans-one-budget' onChange={() => handleOutputFormatChange(OutputFormatOption.ThreePlansOneBudget)} checked={createAIPlan.outputFormat === OutputFormatOption.ThreePlansOneBudget} label='3 plans, 1 budget' />}
      {createAIPlan.outputFormat === OutputFormatOption.ThreePlansOneBudget && (
        <>
          {renderBudgetModeRadios(OutputFormatOption.ThreePlansOneBudget)}
          {isCustomBudget && (
            <div style={{ marginLeft: 48, marginTop: 8 }}>
              <EuiForm>{renderBudgetInputField('Target Monthly Budget', createAIPlan.monthlyBudget, val => dispatch(setMonthlyBudget(val)), false)}</EuiForm>
            </div>
          )}
        </>
      )}
      <EuiRadio id='one-plan' onChange={() => handleOutputFormatChange(OutputFormatOption.OnePlan)} checked={createAIPlan.outputFormat === OutputFormatOption.OnePlan} label='1 plan' />
      {createAIPlan.outputFormat === OutputFormatOption.OnePlan && (
        <>
          {renderBudgetModeRadios(OutputFormatOption.OnePlan)}
          {isCustomBudget && (
            <div style={{ marginLeft: 48, marginTop: 8 }}>
              <EuiForm>{renderBudgetInputField('Target Monthly Budget', createAIPlan.monthlyBudget, val => dispatch(setMonthlyBudget(val)), false)}</EuiForm>
            </div>
          )}
        </>
      )}
    </>
  )

  const adCritterChannelValues = CHANNEL_DEFINITIONS.map(c => c.value)

  const handleChannelToggle = (channelValue: string) => {
    const isSelected = createAIPlan.selectedChannels.includes(channelValue)
    const isAdCritterChannel = adCritterChannelValues.includes(channelValue)
    const selectedAdCritterCount = createAIPlan.selectedChannels.filter(c => adCritterChannelValues.includes(c)).length
    if (isSelected && isAdCritterChannel && selectedAdCritterCount === 1) {
      dispatch(showWarningToast('At least one AdCritter-Integrated channel must be selected.'))
      return
    }
    const updated = isSelected ? createAIPlan.selectedChannels.filter(c => c !== channelValue) : [...createAIPlan.selectedChannels, channelValue]
    dispatch(setSelectedChannels(updated))
  }

  const renderChannelCheckboxes = (idPrefix: string, disabled: boolean, checked: (channelValue: string) => boolean) => (
    <>
      <EuiText size='xs' color='subdued'>
        <strong>AdCritter-Integrated Channels</strong>
      </EuiText>
      <EuiSpacer size='s' />
      {CHANNEL_DEFINITIONS.map(channel => (
        <React.Fragment key={channel.value}>
          <EuiCheckbox id={`${idPrefix}-${channel.value}`} label={channel.label} disabled={disabled} checked={checked(channel.value)} onChange={() => handleChannelToggle(channel.value)} />
          <EuiSpacer size='s' />
        </React.Fragment>
      ))}
      <EuiSpacer size='s' />
      <EuiText size='xs' color='subdued'>
        <strong>Off-AdCritter Channels</strong>
      </EuiText>
      <EuiSpacer size='s' />
      {offAdCritterChannels.map(channel => (
        <React.Fragment key={channel.value}>
          <EuiCheckbox id={`${idPrefix}-${channel.value}`} label={channel.label} disabled={disabled} checked={checked(channel.value)} onChange={() => handleChannelToggle(channel.value)} />
          <EuiSpacer size='s' />
        </React.Fragment>
      ))}
    </>
  )

  const renderChannelsStep = () => (
    <>
      <EuiTitle size='s'>
        <h2>Step {isInstantPlan ? 2 : 3}: Channels</h2>
      </EuiTitle>
      <EuiText>Select which advertising channels to include in your plan.</EuiText>
      <EuiSpacer />
      <EuiRadio
        id='ai-recommended-channels'
        onChange={() => {
          dispatch(setChannelMode(ChannelModeOption.AiRecommended))
          dispatch(setSelectedChannels([]))
        }}
        checked={createAIPlan.channelMode === ChannelModeOption.AiRecommended}
        label='AI Suggested Channels (recommended)'
      />
      <EuiRadio
        id='specify-channels'
        onChange={() => {
          dispatch(setChannelMode(ChannelModeOption.Specific))
          if (createAIPlan.selectedChannels.length === 0) {
            dispatch(setSelectedChannels(CHANNEL_DEFINITIONS.map(c => c.value)))
          }
        }}
        checked={createAIPlan.channelMode === ChannelModeOption.Specific}
        label='Manual Channel Selection'
      />
      {createAIPlan.channelMode === ChannelModeOption.Specific && (
        <div style={{ marginLeft: 24, marginTop: 8 }}>{renderChannelCheckboxes('channel-specify', false, channelValue => createAIPlan.selectedChannels.includes(channelValue))}</div>
      )}
    </>
  )

  const renderPersonasStep = () => (
    <>
      <EuiTitle size='s'>
        <h2>Step {isInstantPlan ? 3 : 4}: Personas</h2>
      </EuiTitle>
      <EuiText>Choose which audience personas {isInstantPlan ? 'to target in your plan' : 'from the blueprint to target in your plan'}.</EuiText>
      <EuiSpacer />
      <EuiRadio id='use-all-personas' onChange={() => dispatch(setPersonaMode(PersonaModeOption.All))} checked={createAIPlan.personaMode === PersonaModeOption.All} label='Use all personas (recommended)' />
      {!isInstantPlan && <EuiRadio id='select-personas' onChange={() => dispatch(setPersonaMode(PersonaModeOption.Selected))} checked={createAIPlan.personaMode === PersonaModeOption.Selected} label='Use selected personas' />}
      <div style={{ marginLeft: 24, marginTop: 4 }}>
        <EuiForm>
          <EuiFormRow>
            <EuiComboBox
              isDisabled={createAIPlan.personaMode !== PersonaModeOption.Selected}
              options={personaOptions}
              selectedOptions={
                createAIPlan.personaMode === PersonaModeOption.Selected
                  ? createAIPlan.selectedPersonas.map(id => {
                      const persona = (selectedBlueprint?.personas ?? []).find(p => p.id === id)
                      return { label: persona?.name ?? id, key: id }
                    })
                  : []
              }
              onChange={selected => dispatch(setSelectedPersonas(selected.map(s => s.key!)))}
              placeholder='Select personas'
            />
          </EuiFormRow>
        </EuiForm>
      </div>
      <EuiRadio id='ignore-personas' onChange={() => dispatch(setPersonaMode(PersonaModeOption.Ignore))} checked={createAIPlan.personaMode === PersonaModeOption.Ignore} label='Ignore personas' />
    </>
  )

  const renderTimeframeStep = () => (
    <>
      <EuiTitle size='s'>
        <h2>Step {isInstantPlan ? 4 : 5}: Timeframe</h2>
      </EuiTitle>
      <EuiText>Set the plan duration and start date.</EuiText>
      <EuiSpacer />
      <EuiRadio id='twelve-months' onChange={() => dispatch(setTimeframeOption(TimeframeOption.TwelveMonths))} checked={createAIPlan.timeframeOption === TimeframeOption.TwelveMonths} label='12 months (recommended)' />
      <EuiRadio id='six-months' onChange={() => dispatch(setTimeframeOption(TimeframeOption.SixMonths))} checked={createAIPlan.timeframeOption === TimeframeOption.SixMonths} label='6 months' />
      <EuiRadio id='other-duration' onChange={() => dispatch(setTimeframeOption(TimeframeOption.Other))} checked={createAIPlan.timeframeOption === TimeframeOption.Other} label='Other' />
      <div style={{ marginLeft: 24, marginTop: 4 }}>
        <EuiForm>
          <EuiFormRow label='Duration (Months)'>
            <EuiFieldNumber disabled={createAIPlan.timeframeOption !== TimeframeOption.Other} value={createAIPlan.timeframeOption === TimeframeOption.Other ? createAIPlan.customDurationMonths ?? '' : ''} onChange={e => dispatch(setCustomDurationMonths(e.target.value ? Number(e.target.value) : undefined))} min={1} />
          </EuiFormRow>
        </EuiForm>
      </div>
      <EuiSpacer />
      <EuiForm>
        <EuiFormRow label='Start Date'>
          <EuiDatePicker fullWidth selected={moment(createAIPlan.planStartDate)} dateFormat='MM/DD/yyyy' onChange={date => date && dispatch(setPlanStartDate(date))} minDate={moment().add(3, 'days')} showTimeSelect={false} />
        </EuiFormRow>
      </EuiForm>
    </>
  )

  const renderAdditionalGuidanceStep = () => (
    <>
      <EuiTitle size='s'>
        <h2>Step {isInstantPlan ? 5 : 6}: Guidance Prompt</h2>
      </EuiTitle>
      <EuiText>Provide any additional context or guidance for your plan. (Optional)</EuiText>
      <EuiSpacer />
      <EuiForm>
        <EuiFormRow label='Guidance Prompt' fullWidth>
          <EuiTextArea fullWidth value={createAIPlan.additionalGuidance} onChange={e => dispatch(setAdditionalGuidance(e.target.value))} placeholder='Any additional context or guidance for your plan. (Optional)' />
        </EuiFormRow>
      </EuiForm>
    </>
  )

  return (
    <EuiPage className='euiPage--platform'>
      <EuiPageBody>
        <EuiPageHeader>
          <EuiPageHeaderSection>
            <EuiTitle>
              <h1>{isInstantPlan ? 'Instant Plan' : 'Create AI Plan'}{currentAccount ? `: ${currentAccount.name}` : ''}</h1>
            </EuiTitle>
          </EuiPageHeaderSection>
        </EuiPageHeader>
        <EuiPanel grow={false} className='eui-xScroll'>
          <EuiFlexGroup>
            <EuiFlexItem>
              <>
                {!isInstantPlan && createAIPlan.step === AIPlanCreateStep.SelectBasis && renderSelectBasisStep()}
                {createAIPlan.step === AIPlanCreateStep.OutputFormat && renderBudgetingStep()}
                {createAIPlan.step === AIPlanCreateStep.Channels && renderChannelsStep()}
                {createAIPlan.step === AIPlanCreateStep.Personas && renderPersonasStep()}
                {createAIPlan.step === AIPlanCreateStep.Timeframe && renderTimeframeStep()}
                {createAIPlan.step === AIPlanCreateStep.AdditionalGuidance && renderAdditionalGuidanceStep()}
                <EuiSpacer />
                {showWarning && (
                  <>
                    <EuiCallOut color='warning' title={warningText} />
                    <EuiSpacer />
                  </>
                )}
                <EuiFlexGroup justifyContent='flexStart'>
                  <EuiFlexItem grow={false}>
                    <EuiButton isDisabled={isCreateLoading} onClick={handleBack}>
                      Back
                    </EuiButton>
                  </EuiFlexItem>
                  <EuiFlexItem grow={false}>
                    <EuiButton fill onClick={handleContinue} isLoading={isCreateLoading} isDisabled={shouldDisableNext()}>
                      {getConfirmText(createAIPlan.step)}
                    </EuiButton>
                  </EuiFlexItem>
                </EuiFlexGroup>
              </>
            </EuiFlexItem>
            <EuiFlexItem grow={false}>
              <EuiFlexGroup
                gutterSize='none'
                direction='column'
                justifyContent='flexStart'
                style={{
                  borderLeft: '1px solid #D3DAE6',
                  paddingLeft: '20px'
                }}>
                <EuiFlexItem grow={false}>
                  <EuiSteps
                    steps={steps}
                    style={{
                      marginTop: '62px',
                      width: 350
                    }}
                  />
                </EuiFlexItem>
              </EuiFlexGroup>
            </EuiFlexItem>
          </EuiFlexGroup>
        </EuiPanel>
      </EuiPageBody>
    </EuiPage>
  )
}
